import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { AreaPrincipal, BarraInferior } from '../components/painel';
import YoutubePlayer from '../components/painel/YoutubePlayer';
import SenhaChamada from '../components/painel/SenhaChamada';
import UltimasSenhas from '../components/painel/UltimasSenhas';
import CartorioLogo from '../components/CartorioLogo';
import useSenhasApi from '../hooks/useSenhasApi';
import VocalizacaoPopup from '../components/painel/VocalizacaoPopup';
import useAudioAutorizacao from '../hooks/useAudioAutorizacao';
import socketService from '../services/socketService';
import speechService from '../services/speechService';
import { toast } from 'react-hot-toast';

// Criar um tema fixo em modo light para o painel de chamada
const painelTheme = createTheme({
  typography: {
    fontFamily: [
      'Montserrat',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  palette: {
    mode: 'light', // Sempre em modo light
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#555555',
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Montserrat, sans-serif',
        },
      },
    },
  },
});

const PainelChamada = () => {
  const {
    senhaAtual,
    guicheAtual,
    preferencialAtual,
    ultimasSenhas,
    dataChamadaAtual,
    carregando,
    erro
  } = useSenhasApi();

  // Usar o hook de autorização de áudio
  const { autorizado, setAutorizacao } = useAudioAutorizacao();
  
  // Estado para controlar a exibição forçada do modal
  const [mostrarModal, setMostrarModal] = useState(true);
  
  // Refs para controle simplificado (apenas timeouts se necessário)
  const timeoutRef = useRef(null); // Ref para controlar os timeouts
  
  // Efeito de limpeza para timeouts
  useEffect(() => {
    // Limpar recursos ao desmontar
    return () => {
      // Limpar qualquer timeout pendente ao desmontar
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Função simplificada para vocalizar uma senha
  const vocalizarSenha = (numeroDaSenha, numeroDoGuiche, preferencial, senhaId = null) => {
    // Verificar se a vocalização está autorizada pelo usuário
    if (!autorizado) {
      console.log('🔇 Vocalização não autorizada pelo usuário, ignorando chamada.');
      return;
    }
    
    // Verificar se esta senha já foi vocalizada recentemente (anti-duplicação de 3 segundos)
    const ultimaSenhaVocalizada = localStorage.getItem('ultima_senha_vocalizada');
    if (ultimaSenhaVocalizada) {
      try {
        const senhaData = JSON.parse(ultimaSenhaVocalizada);
        // Se a mesma senha foi vocalizada nos últimos 3 segundos, pular
        if (senhaData.senha === numeroDaSenha && 
            senhaData.guiche === numeroDoGuiche && 
            (Date.now() - senhaData.timestamp) < 3000) {
          console.log('🔄 Senha já vocalizada recentemente, ignorando duplicação');
          return;
        }
      } catch (e) {
        // Erro ao processar o JSON, continuar com a vocalização
        console.warn('⚠️ Erro ao processar última senha vocalizada:', e);
      }
    }
    
    // Registrar esta senha como a última vocalizada (antes de chamar o serviço)
    localStorage.setItem('ultima_senha_vocalizada', JSON.stringify({
      senha: numeroDaSenha,
      guiche: numeroDoGuiche,
      timestamp: Date.now()
    }));
    
    console.log(`🎵 Solicitando vocalização: Senha ${numeroDaSenha} Guichê ${numeroDoGuiche}`);
    
    // Chamar o serviço de vocalização de forma simplificada (fire-and-forget)
    const sucesso = speechService.vocalizarSenha(
      numeroDaSenha,
      numeroDoGuiche,
      preferencial,
      0, // prioridade normal
      senhaId // BB-130-CB.7 - Passar senhaId para callback
    );
    
    if (sucesso) {
      console.log(`✅ Senha ${numeroDaSenha} adicionada à fila de vocalização`);
    } else {
      console.log(`❌ Falha ao adicionar senha ${numeroDaSenha} à fila de vocalização`);
    }
  };
  
  // Configuração simplificada - o speechService agora gerencia toda a fila internamente
  // Não há mais necessidade de verificadores periódicos aqui

  // Efeito para conectar ao WebSocket e ouvir eventos de senhas chamadas
  useEffect(() => {
    // Conectar ao socket
    socketService.connect();
    
    // Handler para evento de senha chamada via WebSocket
    const handleSenhaChamada = (data) => {
      console.log('📡 Evento senhaChamada recebido:', data);
      
      // Verificar se os dados contêm as informações necessárias e se deve vocalizar
      if (data.vocalizarSenha && data.senha && data.guiche) {
        // BB-130-CB.7 - Capturar senhaId do evento e passar para vocalização
        vocalizarSenha(data.senha, data.guiche, data.preferencial, data.senhaId);
      }
    };

    // Handler para evento de reset diário automático
    const handleResetDiario = (data) => {
      console.log('🕙 Reset diário do sistema recebido:', data);
      
      // Mostrar notificação visual
      toast.info(
        `Reset diário executado às ${data.horaReset || '00h10'}. Painel atualizado para o dia ${data.dataReset}.`,
        {
          duration: 5000,
          position: 'top-center',
          style: {
            background: '#2196f3',
            color: 'white',
            fontSize: '16px',
            padding: '12px 20px',
          },
        }
      );
      
      // Recarregar a página após um pequeno delay para mostrar a notificação
      setTimeout(() => {
        console.log('🔄 Recarregando painel após reset diário...');
        window.location.reload();
      }, 2000);
    };

    // Handler para evento de reinício do sistema
    const handleSistemaReiniciado = (data) => {
      console.log('🔄 Sistema reiniciado:', data);
      
      // Mostrar notificação
      toast.info(
        'Sistema de senhas reiniciado. Recarregando painel...',
        {
          duration: 3000,
          position: 'top-center',
          style: {
            background: '#ff9800',
            color: 'white',
            fontSize: '16px',
            padding: '12px 20px',
          },
        }
      );
      
      // Recarregar após um pequeno delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    };

    // Handler para eventos de atualização geral
    const handleAtualizacao = (data) => {
      console.log('📡 Evento de atualização recebido:', data);
      
      // Se for um reset diário, tratar de forma especial
      if (data.tipo === 'reset_diario') {
        handleResetDiario(data);
      } else if (data.tipo === 'reinicio_sistema') {
        handleSistemaReiniciado(data);
      }
    };
    
    // Registrar todos os handlers
    socketService.on('senhaChamada', handleSenhaChamada);
    socketService.on('reset_diario', handleResetDiario);
    socketService.on('sistema_reiniciado', handleSistemaReiniciado);
    socketService.on('atualizacao', handleAtualizacao);
    
    // O speechService agora gerencia toda a fila internamente
    // Não é mais necessário verificador periódico aqui // Verificar a cada 5 segundos
    
    // Limpar ao desmontar
    return () => {
      // Remover todos os listeners
      socketService.off('senhaChamada', handleSenhaChamada);
      socketService.off('reset_diario', handleResetDiario);
      socketService.off('sistema_reiniciado', handleSistemaReiniciado);
      socketService.off('atualizacao', handleAtualizacao);
      
      // Não há mais verificador periódico para limpar
      
      // Limpar qualquer vocalização pendente
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      
      // Limpar timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [autorizado]);

  // Sistema simplificado - toda a lógica de fila agora é gerenciada pelo speechService
  // Não há mais necessidade de verificações de estado local ou diagnósticos complexos

  // Função para lidar com a autorização de voz
  const handleAutorizacaoVoz = (autoriza) => {
    setAutorizacao(autoriza);
    setMostrarModal(false); // Fecha o modal após a decisão
    
    // Também atualizar o serviço de speech
    speechService.setAutorizacao(autoriza);
    
    if (autoriza) {
      toast.success('Vocalização de senhas autorizada!');
    } else {
      toast.info('Vocalização de senhas desativada.');
    }
  };

  // Efeito para mostrar o modal toda vez que a página for carregada/recarregada
  useEffect(() => {
    // Mostrar o modal de autorização de áudio sempre que a página for carregada
    setMostrarModal(true);
  }, []);

  // Renderização condicional para indicador de carregamento
  if (carregando && !senhaAtual) {
    return (
      <MuiThemeProvider theme={painelTheme}>
        <Box sx={{ 
          height: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}>
          <Typography variant="h5">Carregando painel de atendimento...</Typography>
        </Box>
      </MuiThemeProvider>
    );
  }

  return (
    <MuiThemeProvider theme={painelTheme}>
      <Box sx={{ 
        position: 'relative',
        height: '100vh', 
        bgcolor: 'background.default', 
        display: 'flex', 
        flexDirection: 'row'
      }}>
        {/* Pop-up de autorização de áudio (aparece apenas se não estiver autorizado) */}
        {mostrarModal && <VocalizacaoPopup onAutorizar={handleAutorizacaoVoz} />}
        
        {/* Botão de diagnóstico invisível - ativado com 5 cliques rápidos no topo da tela */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '50px', 
            height: '50px', 
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={(e) => {
            // Para depuração - 5 cliques rápidos para diagnosticar
            const now = Date.now();
            const clickHistory = e.currentTarget.dataset.clicks 
              ? JSON.parse(e.currentTarget.dataset.clicks) 
              : [];
            
            // Adicionar timestamp do clique
            clickHistory.push(now);
            
            // Manter apenas os últimos 5 cliques
            while (clickHistory.length > 5) {
              clickHistory.shift();
            }
            
            // Salvar histórico
            e.currentTarget.dataset.clicks = JSON.stringify(clickHistory);
            
            // Verificar se houve 5 cliques em menos de 3 segundos
            if (clickHistory.length === 5 && 
                (clickHistory[4] - clickHistory[0]) < 3000) {
              // Limpar histórico
              e.currentTarget.dataset.clicks = JSON.stringify([]);
              
              // Executar diagnóstico - agora usando o comando global
              if (window.debugVocalizacao) {
                window.debugVocalizacao();
              } else {
                console.log('🔍 Diagnóstico do sistema de vocalização:', speechService.getStatus());
              }
            }
          }}
        />
        
        {/* Lado esquerdo: Vídeo (16:9) + Barra Inferior dinâmica */}
        <Box sx={{ 
          width: '58%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Área do vídeo - altura dinâmica baseada na largura (16:9) */}
          <Box sx={{ 
            flex: '0 0 auto',
            width: '100%'
          }}>
            <YoutubePlayer />
          </Box>

          {/* Barra Inferior - preenche o espaço restante */}
          <BarraInferior />
        </Box>
        
        {/* Lado direito: Área de Senhas */}
        <Box sx={{ 
          width: '42%',
          height: '100vh',
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          position: 'relative'
        }}>
          {/* Indicador de erro de conexão */}
          {erro && (
            <Box sx={{ 
              position: 'absolute', 
              top: 5, 
              left: 10, 
              zIndex: 10,
              bgcolor: 'rgba(244, 67, 54, 0.9)',
              color: 'white',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              ERRO DE CONEXÃO
            </Box>
          )}
          
          <CartorioLogo alignment="center" sx={{ mb: 1 }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold', 
              color: 'text.primary', 
              textAlign: 'center',
              mb: 2
            }}
          >
            Painel de Atendimento
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              width: '100%',
              border: '1px solid #e9ecef', 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper'
            }}
          >
            <SenhaChamada 
              senha={senhaAtual} 
              guiche={guicheAtual} 
              preferencial={preferencialAtual} 
              horarioChamada={dataChamadaAtual}
              vocalizacaoAutorizada={autorizado}
            />
            <Divider />
            <UltimasSenhas senhas={ultimasSenhas} />
          </Paper>
        </Box>
      </Box>
    </MuiThemeProvider>
  );
};

export default PainelChamada; 